<script setup lang="ts">
import { computed } from 'vue'
import { ElTag } from 'element-plus'
import { getStatusType, statusText } from './StatusUtils'
import { AppointmentStatus, ContainerWatchState } from '~/services/apiClient'
import type { AppointmentWithContainerInfo } from '~/models/appointmentWithContainerInfo'
import type { Appointment } from '~/models/appointments'
import type { AppointmentTransaction } from '~/models/groupedAppointments'
import DualFullOutFullIn from '~/components/icons/DualFullOutFullIn.vue'
import DualEmptyOutEmptyIn from '~/components/icons/DualEmptyOutEmptyIn.vue'
import DualFullOutEmptyIn from '~/components/icons/DualFullOutEmptyIn.vue'
import DualEmptyOutFullIn from '~/components/icons/DualEmptyOutFullIn.vue'

const props = defineProps<{
  status: AppointmentStatus
  containerInfo?: AppointmentWithContainerInfo
  moveType?: string
  appointment?: Appointment
  isDual?: boolean
  transaction?: AppointmentTransaction
}>()

const displayStatus = computed(() => {
  if (
    props.moveType === 'Load Out' &&
    props.containerInfo?.watch?.state === ContainerWatchState.ImportPickedUp
  ) {
    return AppointmentStatus.Completed
  }
  return props.status
})

const moveTypeIcon = computed(() => {
  if (props.isDual && props.transaction) {
    // For dual transactions, determine the icon based on inbound and outbound moves
    const inbound = props.transaction.inbound_appointment?.appointment
    const outbound = props.transaction.outbound_appointment?.appointment

    if (inbound && outbound) {
      // Determine the move types for both directions
      const inboundIsLoaded = inbound.loaded
      const outboundIsLoaded = outbound.loaded

      // Return a combination that represents both moves
      if (inboundIsLoaded && outboundIsLoaded) {
        return 'dual-full-out-full-in' // Both loaded - filled arrows
      } else if (!inboundIsLoaded && !outboundIsLoaded) {
        return 'dual-empty-out-empty-in' // Both empty - empty arrows
      } else if (!inboundIsLoaded && outboundIsLoaded) {
        return 'dual-full-out-empty-in' // Empty in, loaded out
      } else {
        return 'dual-empty-out-full-in' // Loaded in, empty out
      }
    }
    return 'dual' // Fallback for dual without transaction details
  }

  if (!props.appointment) {
    return null
  }

  const isLoaded = props.appointment.loaded
  const isInbound = props.appointment.direction === 'inbound'

  if (isLoaded && isInbound) return 'loaded-in'
  if (isLoaded && !isInbound) return 'loaded-out'
  if (!isLoaded && isInbound) return 'empty-in'
  if (!isLoaded && !isInbound) return 'empty-out'

  return null
})

const tooltipText = computed(() => {
  const statusDisplayText = statusText(displayStatus.value)
  let moveTypeText = props.moveType || 'Unknown'

  // For dual transactions, provide more descriptive move type text
  if (props.isDual && props.transaction) {
    const inbound = props.transaction.inbound_appointment?.appointment
    const outbound = props.transaction.outbound_appointment?.appointment

    if (inbound && outbound) {
      const inboundDesc = inbound.loaded ? 'Load' : 'Empty'
      const outboundDesc = outbound.loaded ? 'Load' : 'Empty'
      moveTypeText = `${outboundDesc} Out, ${inboundDesc} In`
    }
  }

  return `${statusDisplayText} ${moveTypeText}`
})
</script>

<template>
  <ElTag
    v-tooltip="tooltipText"
    :type="getStatusType(displayStatus)"
    round
    disable-transitions
    effect="plain"
    :class="{ scheduled: displayStatus === AppointmentStatus.Scheduled }"
  >
    <div class="flex items-center gap-1">
      <span>{{ statusText(displayStatus) }}</span>
      <span class="move-type-icon">
        <!-- Custom dual transaction icons -->
        <DualFullOutFullIn v-if="moveTypeIcon === 'dual-full-out-full-in'" />
        <DualEmptyOutEmptyIn
          v-if="moveTypeIcon === 'dual-empty-out-empty-in'"
        />
        <DualFullOutEmptyIn v-if="moveTypeIcon === 'dual-full-out-empty-in'" />
        <DualEmptyOutFullIn v-if="moveTypeIcon === 'dual-empty-out-full-in'" />

        <!-- Fallback dual icon -->
        <i-tabler:arrows-right-left v-if="moveTypeIcon === 'dual'" />

        <!-- Single transaction icons -->
        <i-tabler:arrow-big-right-filled v-if="moveTypeIcon === 'loaded-out'" />
        <i-tabler:arrow-big-left-filled v-if="moveTypeIcon === 'loaded-in'" />
        <i-tabler:arrow-big-right v-if="moveTypeIcon === 'empty-out'" />
        <i-tabler:arrow-big-left v-if="moveTypeIcon === 'empty-in'" />
      </span>
    </div>
  </ElTag>
</template>

<style scoped lang="scss">
$background-color: #fefcff;
$text-color: #d38af2;

.scheduled {
  background-color: $background-color !important;
  color: $text-color;
  border-color: $text-color;
}

.move-type-icon {
  @apply text-sm;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
</style>
